// src/components/Assets/AssetPermissions/index.js
// Asset permissions list with approve/deny functionality

import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAssetPermissions } from "../hooks/useAssetPermissions";
import PermissionCard from "./PermissionCard";
import ConfirmationModal from "../ConfirmationModal";
import "../style.css";
import "./PermissionStyles.css";

const AssetPermissions = () => {
  const [t] = useTranslation();
  const { permissions, loading, approvePermission, denyPermission } =
    useAssetPermissions("admin");

  const [statusFilter, setStatusFilter] = useState("all"); // all, pending, approved, denied
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: null, // "approve" or "deny"
    permission: null,
    isSubmitting: false
  });

  // Handle approve action with confirmation
  const handleApprove = (permissionId) => {
    const permission = permissions.find(p => p.id === permissionId);
    setConfirmModal({
      isOpen: true,
      type: "approve",
      permission,
      isSubmitting: false
    });
  };

  // Handle deny action with confirmation
  const handleDeny = (permissionId) => {
    const permission = permissions.find(p => p.id === permissionId);
    setConfirmModal({
      isOpen: true,
      type: "deny",
      permission,
      isSubmitting: false
    });
  };

  // Execute the confirmation action
  const handleConfirmAction = async () => {
    setConfirmModal(prev => ({ ...prev, isSubmitting: true }));

    if (confirmModal.type === "approve") {
      await approvePermission(confirmModal.permission.id);
    } else if (confirmModal.type === "deny") {
      await denyPermission(confirmModal.permission.id);
    }

    setConfirmModal({
      isOpen: false,
      type: null,
      permission: null,
      isSubmitting: false
    });
  };

  // Close modal
  const handleCloseModal = () => {
    if (!confirmModal.isSubmitting) {
      setConfirmModal({
        isOpen: false,
        type: null,
        permission: null,
        isSubmitting: false
      });
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString(t("locale") === "ar" ? "ar-EG" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  // Get modal message with context
  const getModalMessage = () => {
    if (!confirmModal.permission) return "";

    const permission = confirmModal.permission;
    const assetName = permission.asset?.name || t("Unknown Asset");
    const farmName = permission.farm?.name || permission.farm?.farmName || permission.farmName || t("Unknown Farm");
    const startDate = formatDate(permission.requestDate);
    const endDate = formatDate(permission.requestEndDate);
    const hasUsageTime = permission.usageTime && permission.usageTime > 0;

    if (confirmModal.type === "approve") {
      if (hasUsageTime) {
        return t("You are about to approve a request to use {{assetName}} for {{farmName}}. The requested period is from {{startDate}} to {{endDate}} for {{usageTime}} hours. The asset will be available for use during this period.", {
          assetName,
          farmName,
          startDate,
          endDate,
          usageTime: permission.usageTime
        });
      } else {
        return t("You are about to approve a request to use {{assetName}} for {{farmName}}. The requested period is from {{startDate}} to {{endDate}}. The asset will be available for use during this period.", {
          assetName,
          farmName,
          startDate,
          endDate
        });
      }
    } else {
      return t("You are about to deny a request to use {{assetName}} for {{farmName}}. The farmer will be notified that their request from {{startDate}} to {{endDate}} has been rejected.", {
        assetName,
        farmName,
        startDate,
        endDate
      });
    }
  };

  // Filter permissions based on selected status
  const filteredPermissions = useMemo(() => {
    if (statusFilter === "all") {
      return permissions;
    }
    return permissions.filter(p => p.status === statusFilter);
  }, [permissions, statusFilter]);

  // Count permissions by status
  const counts = useMemo(() => {
    return {
      all: permissions.length,
      pending: permissions.filter(p => p.status === "pending").length,
      approved: permissions.filter(p => p.status === "approved").length,
      denied: permissions.filter(p => p.status === "denied").length
    };
  }, [permissions]);

  // Loading state
  if (loading) {
    return (
      <div className="permissions-container">
        <div className="loading-spinner">
          {t("Loading...")}
        </div>
      </div>
    );
  }

  return (
    <div className="permissions-container">
      {/* Status Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`filter-tab ${statusFilter === "all" ? "active" : ""}`}
          onClick={() => setStatusFilter("all")}
        >
          {t("All")} ({counts.all})
        </button>
        <button
          className={`filter-tab ${statusFilter === "pending" ? "active" : ""}`}
          onClick={() => setStatusFilter("pending")}
        >
          {t("Pending")} ({counts.pending})
        </button>
        <button
          className={`filter-tab ${statusFilter === "approved" ? "active" : ""}`}
          onClick={() => setStatusFilter("approved")}
        >
          {t("Approved")} ({counts.approved})
        </button>
        <button
          className={`filter-tab ${statusFilter === "denied" ? "active" : ""}`}
          onClick={() => setStatusFilter("denied")}
        >
          {t("Denied")} ({counts.denied})
        </button>
      </div>

      {/* Empty State */}
      {filteredPermissions.length === 0 ? (
        <div className="empty-state">
          <p>
            {statusFilter === "all"
              ? t("No permission requests")
              : t(`No ${statusFilter} permission requests`)}
          </p>
          <p style={{ fontSize: "0.9rem", marginTop: "8px", color: "#999" }}>
            {t("Permission requests from farmers will appear here")}
          </p>
        </div>
      ) : (
        /* Permissions List */
        <div className="permissions-list">
          {filteredPermissions.map((permission) => (
            <PermissionCard
              key={permission.id}
              permission={permission}
              onApprove={handleApprove}
              onDeny={handleDeny}
              showActions={permission.status === "pending"}
            />
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmAction}
        title={
          confirmModal.type === "approve"
            ? t("Approve Asset Request")
            : t("Deny Asset Request")
        }
        message={getModalMessage()}
        confirmText={
          confirmModal.type === "approve" ? t("Approve") : t("Deny")
        }
        cancelText={t("Cancel")}
        type={confirmModal.type === "approve" ? "success" : "danger"}
        isSubmitting={confirmModal.isSubmitting}
      />
    </div>
  );
};

export default AssetPermissions;
